/**
 * OtpPage — 6-digit OTP entry, mirrors Flutter `splash/otp_page.dart`.
 * On success the user is redirected to mode-selector (or home if mode is set).
 */
import { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useModeStore } from '@/stores/modeStore';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';

export default function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyCode, sendCode, loading } = useAuthStore();
  const { mode } = useModeStore();
  const phone = (location.state as { phone?: string } | null)?.phone;
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!phone) return <Navigate to="/login" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{4,8}$/.test(code)) {
      setError(t('otp_invalid', 'كود غير صحيح'));
      return;
    }
    try {
      await verifyCode(phone!, code);
      navigate(mode ? '/home' : '/mode-selector', { replace: true });
    } catch (err: any) {
      setError(err?.message || t('otp_failed', 'فشل التحقق'));
    }
  }

  async function handleResend() {
    setError(null);
    try {
      await sendCode(phone!);
    } catch (err: any) {
      setError(err?.message || t('resend_failed', 'فشل إعادة الإرسال'));
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-surface rounded-2xl shadow-md p-8">
        <h1 className="text-2xl font-bold text-primary mb-2 text-center">
          {t('verify_code', 'تأكيد الرقم')}
        </h1>
        <p className="text-text-secondary text-center mb-8" dir="ltr">
          {phone}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            label={t('otp_code', 'الكود')}
            type="text"
            inputMode="numeric"
            dir="ltr"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="123456"
            error={error ?? undefined}
            autoFocus
            maxLength={8}
          />
          <Button type="submit" loading={loading} fullWidth>
            {t('verify', 'تحقّق')}
          </Button>
          <button
            type="button"
            onClick={handleResend}
            className="block w-full text-sm text-primary hover:underline"
          >
            {t('resend_code', 'إعادة إرسال الكود')}
          </button>
        </form>
      </div>
    </div>
  );
}
